import { useState, useEffect } from "react";
import {
  UserOutlined,
  LockOutlined,
  BankOutlined,
  CodeOutlined,
  DeleteOutlined,
  PlusOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined
} from "@ant-design/icons";
import { motion, AnimatePresence } from "framer-motion";
import { Alert, Modal } from "antd";
import {
  useCreateBankAccountMutation,
  useDeleteBankAccountMutation,
  useGetBankAccountQuery,
} from "../../../redux/rtk/apiRtk";

const BankManagement = () => {
  // Theme color
  const primaryColor = '#A40165';

  // Animation state
  const [animateSection, setAnimateSection] = useState(false);
  const [activeTab, setActiveTab] = useState("list"); // "list" or "form"
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // Form state
  const [formData, setFormData] = useState({
    accountHolderName: "",
    accountNumber: "",
    bankName: "",
    ifscCode: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // API hooks
  const { data: bankAccounts, refetch, isLoading, isError, error } =
    useGetBankAccountQuery("");

  const [createBankAccount] = useCreateBankAccountMutation();
  const [deleteBankAccount] = useDeleteBankAccountMutation();



  // Delete confirmation state
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState(null);

  // Trigger animation after component mounts
  useEffect(() => {
    setTimeout(() => {
      setAnimateSection(true);
    }, 100);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Sanitize input to prevent XSS
    const sanitizedValue = value.replace(/[<>{}]/g, "");
    setFormData({ ...formData, [name]: sanitizedValue });

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.accountHolderName.trim())
      newErrors.accountHolderName = "Account holder name is required";
    if (!formData.accountNumber || !/^\d{9,18}$/.test(formData.accountNumber))
      newErrors.accountNumber =
        "Valid account number (9-18 digits) is required";
    if (!formData.bankName.trim()) newErrors.bankName = "Bank name is required";
    if (!formData.ifscCode || !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifscCode))
      newErrors.ifscCode = "Valid IFSC code (e.g., SBIN0001234) is required";
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    setIsSubmitting(true);

    try {
      const response = await createBankAccount(formData).unwrap();
      console.log("Form submitted successfully:", response);

      // Reset form
      setFormData({
        accountHolderName: "",
        accountNumber: "",
        bankName: "",
        ifscCode: "",
      });

      // Show success message
      setSuccessMessage("Bank account added successfully!");
      setShowSuccessMessage(true);
      setTimeout(() => {
        setShowSuccessMessage(false);
        setActiveTab("list");
      }, 2000);

      // Refresh the bank accounts list
      refetch();
    } catch (error) {
      setErrors({ submit: "Failed to submit form. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  // State for inline delete confirmation
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const handleDeleteClick = (account) => {
    // For modal approach
    setAccountToDelete(account);
    setDeleteConfirmVisible(true);

    // For inline approach (like original BankList)
    setDeleteConfirmId(account.id);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteBankAccount().unwrap();
      setDeleteConfirmVisible(false);
      setAccountToDelete(null);
      setDeleteConfirmId(null);

      // Show success message
      setSuccessMessage("Bank account deleted successfully!");
      setShowSuccessMessage(true);
      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 2000);

      // Refresh the bank accounts list
      refetch();
    } catch (error) {
      console.error("Error deleting bank account:", error);
    }
  };

  const handleInlineDelete = async () => {
    try {
      await deleteBankAccount().unwrap();
      setDeleteConfirmId(null);

      // Show success message
      setSuccessMessage("Bank account deleted successfully!");
      setShowSuccessMessage(true);
      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 2000);

      // Refresh the bank accounts list
      refetch();
    } catch (error) {
      console.error("Error deleting bank account:", error);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmVisible(false);
    setAccountToDelete(null);
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.5,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 py-6 md:py-8 ">
      {/* Success message */}
      <AnimatePresence>
        {showSuccessMessage && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-4 right-4 z-50 bg-green-50 border border-green-200 rounded-lg shadow-lg p-4 flex items-center"
          >
            <CheckCircleOutlined className="text-green-500 text-xl mr-2" />
            <span className="text-green-800">{successMessage}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete confirmation modal */}
      <Modal
        title={
          <div className="flex items-center text-red-600">
            <ExclamationCircleOutlined className="mr-2" /> Confirm Deletion
          </div>
        }
        open={deleteConfirmVisible}
        onOk={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{
          style: { backgroundColor: '#f56565', borderColor: '#f56565' },
          className: 'hover:bg-red-700'
        }}
      >
        <p>Are you sure you want to delete this bank account?</p>
        {accountToDelete && (
          <div className="mt-2 p-3 bg-gray-50 rounded-md">
            <p className="font-medium">{accountToDelete.accountHolderName}</p>
            <p className="text-sm text-gray-600">Account: {accountToDelete.accountNumber}</p>
            <p className="text-sm text-gray-600">Bank: {accountToDelete.bankName}</p>
          </div>
        )}
        <p className="mt-3 text-red-600 text-sm">This action cannot be undone.</p>
      </Modal>

      {/* Main content */}
      <div
        className={`relative overflow-hidden rounded-2xl shadow-lg transition-all duration-700 transform ${animateSection ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
          }`}
        style={{
          background: `linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)`,
          boxShadow: "0 10px 40px rgba(164, 1, 101, 0.1)",
        }}
      >
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-pink-100 to-transparent rounded-bl-full opacity-70"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-pink-100 to-transparent rounded-tr-full opacity-70"></div>
        <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-gradient-to-tl from-pink-200 to-transparent rounded-tl-full opacity-50"></div>

        <div className="relative z-10 p-6 sm:p-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 md:mb-8">
            <div className="flex items-center mb-4 md:mb-0">
              <div
                className="p-3 rounded-full mr-4"
                style={{ background: `${primaryColor}15` }}
              >
                <BankOutlined
                  className="text-2xl"
                  style={{ color: primaryColor }}
                />
              </div>
              <h2
                className="text-2xl font-bold"
                style={{ color: primaryColor }}
              >
                Bank Account Management
              </h2>
            </div>

            {/* Tab buttons */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setActiveTab("list")}
                className={`px-4 py-2 rounded-md transition-all duration-300 ${activeTab === "list"
                  ? `text-white shadow-md`
                  : "text-gray-600 hover:text-gray-800"
                  }`}
                style={{
                  backgroundColor: activeTab === "list" ? primaryColor : "transparent",
                }}
              >
                My Accounts
              </button>
              <button
                onClick={() => setActiveTab("form")}
                className={`px-4 py-2 rounded-md transition-all duration-300 ${activeTab === "form"
                  ? `text-white shadow-md`
                  : "text-gray-600 hover:text-gray-800"
                  }`}
                style={{
                  backgroundColor: activeTab === "form" ? primaryColor : "transparent",
                }}
              >
                Add New
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="mt-4">
            {activeTab === "list" ? (
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="space-y-6"
              >
                {/* Loading state */}
                {isLoading && (
                  <motion.div
                    variants={itemVariants}
                    className="bg-white rounded-xl p-8 text-center shadow-md border border-gray-100"
                  >
                    <div className="w-12 h-12 mx-auto mb-4 rounded-full border-4 border-t-transparent animate-spin"
                      style={{ borderColor: `${primaryColor} transparent transparent transparent` }}
                    ></div>
                    <p className="text-gray-600">Loading bank accounts...</p>
                  </motion.div>
                )}
                {/* Error state */}
                {!isLoading && isError && (
                  <motion.div
                    variants={itemVariants}
                    className="bg-white rounded-xl p-8 text-center shadow-md border border-gray-100"
                  >
                    <p className="text-red-500 mb-4">
                      Failed to load bank accounts
                      {error?.status ? ` (${error.status})` : ""}. Please check
                      your connection and try again.
                    </p>
                    <button
                      type="button"
                      onClick={() => refetch()}
                      className="px-4 py-2 rounded-md text-white"
                      style={{ backgroundColor: primaryColor }}
                    >
                      Retry
                    </button>
                  </motion.div>
                )}
                {/* Bank accounts list */}
                {!isLoading && !isError && (!bankAccounts || bankAccounts?.length === 0) ? (
                  <motion.div
                    variants={itemVariants}
                    className="bg-white rounded-xl p-8 text-center shadow-md border border-gray-100"
                  >
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ background: `${primaryColor}15` }}>
                      <BankOutlined style={{ fontSize: 24, color: primaryColor }} />
                    </div>
                    <h3 className="text-lg font-medium text-gray-800 mb-2">No Bank Accounts</h3>
                    <p className="text-gray-500 mb-6">You haven't added any bank accounts yet.</p>
                    <button
                      onClick={() => setActiveTab("form")}
                      className="inline-flex items-center px-4 py-2 rounded-lg shadow-md transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98]"
                      style={{
                        background: primaryColor,
                        color: "white",
                        boxShadow: `0 4px 14px 0 rgba(164, 1, 101, 0.25)`,
                      }}
                    >
                      <PlusOutlined className="mr-2" />
                      Add Bank Account
                    </button>
                  </motion.div>
                ) : (
                  !isError &&
                  bankAccounts?.map((account, index) => (
                    <motion.div
                      key={account.id || index}
                      variants={itemVariants}
                      className="bg-white rounded-xl shadow-md border border-gray-100 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 overflow-hidden"
                    >
                      {deleteConfirmId === account.id ? (
                        // Delete confirmation UI (inline style like original)
                        <Alert
                          showIcon
                          message="Do you want to delete this account?"
                          className="border-0 rounded-none"
                          type="warning"
                          action={
                            <div className="flex space-x-2">
                              <button
                                onClick={handleInlineDelete}
                                className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                              >
                                Delete
                              </button>
                              <button
                                onClick={() => setDeleteConfirmId(null)}
                                className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
                              >
                                Cancel
                              </button>
                            </div>
                          }
                        />
                      ) : (
                        // Normal account display
                        <div className="p-4 sm:p-6">
                          <div className="flex flex-col sm:flex-row justify-between items-start">
                            <div className="flex items-start w-full">
                              <div
                                className="p-3 rounded-full mr-4 self-center"
                                style={{ background: `${primaryColor}15` }}
                              >
                                <BankOutlined
                                  className="text-xl"
                                  style={{ color: primaryColor }}
                                />
                              </div>
                              <div className="w-full">
                                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                                  {account.accountHolderName}
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  <div>
                                    <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">
                                      Account Number
                                    </p>
                                    <p className="text-gray-700">
                                      {account.accountNumber}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">
                                      Bank Name
                                    </p>
                                    <p className="text-gray-700">{account.bankName}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">
                                      IFSC Code
                                    </p>
                                    <p className="text-gray-700">{account.ifscCode}</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <button
                              onClick={() => handleDeleteClick(account)}
                              className="text-gray-400 hover:text-red-600 transition-colors duration-300 p-2 rounded-full hover:bg-red-50 mt-2 sm:mt-0 self-end sm:self-start"
                              aria-label="Delete bank account"
                            >
                              <DeleteOutlined className="text-lg" />
                            </button>
                          </div>
                        </div>
                      )}
                    </motion.div>
                  ))
                )}
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100"
              >
                <div className="p-6 border-b border-gray-100">
                  <h3 className="text-lg font-semibold" style={{ color: primaryColor }}>
                    Add New Bank Account
                  </h3>
                </div>
                <div className="p-6">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label
                        htmlFor="accountHolderName"
                        className="block text-sm font-medium text-gray-700 flex items-center"
                      >
                        <div className="p-2 rounded-full mr-2" style={{ background: `${primaryColor}15` }}>
                          <UserOutlined style={{ color: primaryColor }} />
                        </div>
                        Account Holder Name
                      </label>
                      <input
                        type="text"
                        id="accountHolderName"
                        name="accountHolderName"
                        value={formData.accountHolderName}
                        onChange={handleChange}
                        className={`mt-1 w-full px-4 py-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:border-transparent transition duration-200 ${errors.accountHolderName
                          ? "border-red-300 focus:ring-red-200"
                          : "border-gray-300 focus:ring-2"
                          }`}
                        style={{
                          "--tw-ring-color": errors.accountHolderName
                            ? "rgba(252, 165, 165, 0.5)"
                            : `${primaryColor}40`,
                        }}
                        placeholder="Enter account holder name"
                        autoComplete="off"
                      />
                      {errors.accountHolderName && (
                        <p className="mt-1 text-sm text-red-600 flex items-center">
                          <ExclamationCircleOutlined className="mr-1" />
                          {errors.accountHolderName}
                        </p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="accountNumber"
                        className="block text-sm font-medium text-gray-700 flex items-center"
                      >
                        <div className="p-2 rounded-full mr-2" style={{ background: `${primaryColor}15` }}>
                          <LockOutlined style={{ color: primaryColor }} />
                        </div>
                        Account Number
                      </label>
                      <input
                        type="text"
                        id="accountNumber"
                        name="accountNumber"
                        value={formData.accountNumber}
                        onChange={handleChange}
                        className={`mt-1 w-full px-4 py-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:border-transparent transition duration-200 ${errors.accountNumber
                          ? "border-red-300 focus:ring-red-200"
                          : "border-gray-300 focus:ring-2"
                          }`}
                        style={{
                          "--tw-ring-color": errors.accountNumber
                            ? "rgba(252, 165, 165, 0.5)"
                            : `${primaryColor}40`,
                        }}
                        placeholder="Enter account number (9-18 digits)"
                        autoComplete="off"
                      />
                      {errors.accountNumber && (
                        <p className="mt-1 text-sm text-red-600 flex items-center">
                          <ExclamationCircleOutlined className="mr-1" />
                          {errors.accountNumber}
                        </p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="bankName"
                        className="block text-sm font-medium text-gray-700 flex items-center"
                      >
                        <div className="p-2 rounded-full mr-2" style={{ background: `${primaryColor}15` }}>
                          <BankOutlined style={{ color: primaryColor }} />
                        </div>
                        Bank Name
                      </label>
                      <input
                        type="text"
                        id="bankName"
                        name="bankName"
                        value={formData.bankName}
                        onChange={handleChange}
                        className={`mt-1 w-full px-4 py-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:border-transparent transition duration-200 ${errors.bankName
                          ? "border-red-300 focus:ring-red-200"
                          : "border-gray-300 focus:ring-2"
                          }`}
                        style={{
                          "--tw-ring-color": errors.bankName
                            ? "rgba(252, 165, 165, 0.5)"
                            : `${primaryColor}40`,
                        }}
                        placeholder="Enter bank name"
                        autoComplete="off"
                      />
                      {errors.bankName && (
                        <p className="mt-1 text-sm text-red-600 flex items-center">
                          <ExclamationCircleOutlined className="mr-1" />
                          {errors.bankName}
                        </p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="ifscCode"
                        className="block text-sm font-medium text-gray-700 flex items-center"
                      >
                        <div className="p-2 rounded-full mr-2" style={{ background: `${primaryColor}15` }}>
                          <CodeOutlined style={{ color: primaryColor }} />
                        </div>
                        IFSC Code
                      </label>
                      <input
                        type="text"
                        id="ifscCode"
                        name="ifscCode"
                        value={formData.ifscCode}
                        onChange={handleChange}
                        className={`mt-1 w-full px-4 py-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:border-transparent transition duration-200 ${errors.ifscCode
                          ? "border-red-300 focus:ring-red-200"
                          : "border-gray-300 focus:ring-2"
                          }`}
                        style={{
                          "--tw-ring-color": errors.ifscCode
                            ? "rgba(252, 165, 165, 0.5)"
                            : `${primaryColor}40`,
                        }}
                        placeholder="Enter IFSC code (e.g., SBIN0001234)"
                        autoComplete="off"
                      />
                      {errors.ifscCode && (
                        <p className="mt-1 text-sm text-red-600 flex items-center">
                          <ExclamationCircleOutlined className="mr-1" />
                          {errors.ifscCode}
                        </p>
                      )}
                    </div>

                    {errors.submit && (
                      <Alert
                        message={errors.submit}
                        type="error"
                        showIcon
                        icon={<CloseCircleOutlined />}
                        className="mt-4"
                      />
                    )}

                    <div className="flex flex-col-reverse sm:flex-row justify-end space-y-3 space-y-reverse sm:space-y-0 sm:space-x-3 pt-4">
                      <button
                        type="button"
                        onClick={() => setActiveTab("list")}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors duration-300 w-full sm:w-auto text-center"
                      >
                        Cancel
                      </button>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        type="submit"
                        disabled={isSubmitting}
                        className={`px-6 py-2 rounded-lg text-white font-medium transition duration-200 flex items-center justify-center w-full sm:w-auto ${isSubmitting
                          ? "opacity-70 cursor-not-allowed"
                          : "hover:shadow-md"
                          }`}
                        style={{
                          backgroundColor: primaryColor,
                          boxShadow: isSubmitting ? "none" : `0 4px 14px 0 rgba(164, 1, 101, 0.25)`,
                        }}
                      >
                        {isSubmitting ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                            Saving...
                          </>
                        ) : (
                          <>
                            <PlusOutlined className="mr-2" />
                            Add Account
                          </>
                        )}
                      </motion.button>
                    </div>
                  </form>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>


    </div>
  );
};

export default BankManagement;